import * as React from 'react'

import { Box, Tooltip, useToggle } from '@factorialco/deprecated-design-system'
import type moment from 'moment'

import { observer } from 'mobx-react'

import { minutesToText } from 'modules/core/lib/duration'
import i18n from 'modules/core/lib/i18n'

import type { TimeUnit } from 'modules/attendance/store/models/Period'
import Period from 'modules/attendance/store/models/Period'

import MinutesPerDay from './MinutesPerDay'

interface Props {
  startOn: moment.Moment
  period: Period
  wide?: boolean
  windowSize?: number
}
interface PropsMinutesWithTooltips {
  startOn: moment.Moment
  day: number
  minutes: number
  timeUnit: TimeUnit
}

const MinutesWithTooltips = ({
  minutes,
  day,
  startOn,
  timeUnit,
  children,
}: React.PropsWithChildren<PropsMinutesWithTooltips>) => {
  const [showTooltip, tooltipToggle] = useToggle(false)
  const date = startOn.clone().add(day - 1, 'days')
  let description = ''

  switch (timeUnit) {
    case Period.TIME_UNIT.half_day:
      description =
        minutes === 1 ? i18n.t('time_tracking.half_day') : i18n.t('time_tracking.full_day')
      break

    case Period.TIME_UNIT.minute:
      description = minutesToText(minutes)
      break
  }

  return (
    <Box flexDirection="row" gap="s8">
      <Tooltip
        attachment="bottom center"
        offset="20 0"
        type="primary"
        size="small"
        open={showTooltip}
        toggle={tooltipToggle}
      >
        {children}
        <div>
          {date.format('Do MMMM')} –&nbsp;
          {description}
        </div>
      </Tooltip>
    </Box>
  )
}

const MinutesDistribution = ({ startOn, period, wide, windowSize }: Props) => {
  const [isHovering, toggleHovering] = useToggle(false)

  const minutesPerDay = (minutes: number, day: number) => {
    return (
      <MinutesPerDay
        baselineMinutes={period.baselineMinutes}
        estimationMinutesBaseline={period.estimationPerDay(day)}
        key={day}
        minutes={minutes}
        wide={wide}
        windowSize={windowSize}
      />
    )
  }

  // For performance reasons with only enable tooltips on the distribution
  // graph when the user mouse is over it
  const renderDistribution = () =>
    period.attributes.distribution.map((minutes: number, index: number) => {
      const dayIndex = index + 1

      return isHovering ? (
        <MinutesWithTooltips
          key={dayIndex}
          minutes={minutes}
          day={dayIndex}
          timeUnit={period.timeUnitByDay(dayIndex)}
          startOn={startOn}
        >
          {minutesPerDay(minutes, dayIndex)}
        </MinutesWithTooltips>
      ) : (
        minutesPerDay(minutes, dayIndex)
      )
    })

  return (
    <Box
      flexDirection="row"
      alignItems="flexEnd"
      onMouseEnter={toggleHovering(true)}
      onMouseLeave={toggleHovering(false)}
      style={{ height: '50px' }}
    >
      {renderDistribution()}
    </Box>
  )
}

export default observer(MinutesDistribution)
